import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { ArrowRight } from 'lucide-react';
import { requestPasswordReset } from '../services/auth';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Enter the email address linked to your account.');
      return;
    }

    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'We could not send a reset link right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Password reset"
        title="Forgot your password?"
        description="Enter your email address and we will send you a reset link."
      />

      {submitted ? (
        <Stack spacing={3}>
          <Alert severity="success">
            If an account exists for <strong>{email}</strong>, a password reset link has been sent.
          </Alert>
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.72 }}>
            Check your inbox and spam folder, then follow the link to choose a new password.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button component={Link} to="/login" variant="contained" sx={publicPrimaryButtonSx}>
              Return to login
            </Button>
            <Button variant="outlined" onClick={() => setSubmitted(false)} sx={publicSecondaryButtonSx}>
              Send another link
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              required
              fullWidth
              autoFocus
              id="email"
              label="Email address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              helperText="Use the same email address you sign in with."
              sx={publicTextFieldSx}
            />
            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={publicPrimaryButtonSx}>
              {loading ? 'Sending reset link...' : 'Send reset link'}
            </Button>
          </Stack>
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        sx={{ mt: 3 }}
      >
        <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
          Need to create an account instead?
        </Typography>
        <Button component={Link} to="/signup" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
          Sign up
        </Button>
      </Stack>
    </PublicPageFrame>
  );
}

export default ForgotPasswordPage;
